import { useLocalStorageState } from '@side/pantry';
import { isEqual } from 'lodash';
import { createContext, useCallback, useContext, useMemo } from 'react';
import type { PaymentSchema } from '$api-clients/generated';
import {
  useClearAgentsAndTeamsOnViewAsSwitch,
  useViewReportAs,
} from '../helpers';
import type { ReportingPeriod } from '../types';

export type ReportingTransactionsSchema =
  PaymentSchema.paths['/reporting/transactions']['get'];

type TransactionType = NonNullable<
  NonNullable<
    NonNullable<
      ReportingTransactionsSchema['parameters']['query']
    >['transactionType']
  >[0]
>;

export interface TransactionTypeOption {
  label: string;
  value: TransactionType;
}

interface FilterOption {
  label: string;
  value: string;
}

const CURRENT_YEAR = new Date().getFullYear();

export interface CompensationReportFiltersFormValues {
  reportingPeriod: ReportingPeriod;
  customDateStart?: string;
  customDateEnd?: string;
  agents: FilterOption[];
  teams: FilterOption[];
  transactionType: TransactionType[];
  hideTerminatedAgents: boolean;
  leadSources: FilterOption[];
}

export const compensationReportFiltersDefaultValues: CompensationReportFiltersFormValues =
  {
    reportingPeriod: CURRENT_YEAR,
    agents: [],
    teams: [],
    transactionType: [],
    hideTerminatedAgents: false,
    leadSources: [],
  };

const compensationReportFiltersContext = createContext<{
  filters: CompensationReportFiltersFormValues;
  setFilters: (filters: CompensationReportFiltersFormValues) => void;
}>({
  filters: compensationReportFiltersDefaultValues,
  setFilters: (_filters: CompensationReportFiltersFormValues) => null,
});

export const useCompensationReportFilters = () => {
  const { filters, setFilters } = useContext(compensationReportFiltersContext);

  const filterCount = useMemo(() => {
    const keys = Object.keys(
      compensationReportFiltersDefaultValues,
    ) as (keyof CompensationReportFiltersFormValues)[];
    return keys.filter(
      (key) =>
        !isEqual(filters?.[key], compensationReportFiltersDefaultValues[key]),
    ).length;
  }, [filters]);

  const clearFilters = () => {
    setFilters(compensationReportFiltersDefaultValues);
  };

  return { filters, filterCount, setFilters, clearFilters };
};

function normalizeFilters(
  stored: CompensationReportFiltersFormValues,
): CompensationReportFiltersFormValues {
  const currentYear = new Date().getFullYear();
  const reportingPeriod = stored.reportingPeriod;
  const validPeriod = reportingPeriod ?? currentYear;

  return {
    ...compensationReportFiltersDefaultValues,
    ...stored,
    reportingPeriod: validPeriod,
  };
}

export function CompensationReportFiltersProvider({
  children,
  previousViewReportAs,
}: {
  children: React.ReactNode;
  previousViewReportAs?: string;
}) {
  const viewReportAs = useViewReportAs();
  const [storedFilters, setStoredFilters] =
    useLocalStorageState<CompensationReportFiltersFormValues>(
      `compensationReportFilters-${viewReportAs}`,
      compensationReportFiltersDefaultValues,
    );

  useClearAgentsAndTeamsOnViewAsSwitch(
    previousViewReportAs,
    viewReportAs,
    setStoredFilters,
  );

  const filters = useMemo(
    () => normalizeFilters(storedFilters),
    [storedFilters],
  );

  const setFilters = useCallback(
    (newFilters: CompensationReportFiltersFormValues) => {
      setStoredFilters(normalizeFilters(newFilters));
    },
    [setStoredFilters],
  );

  return (
    <compensationReportFiltersContext.Provider value={{ filters, setFilters }}>
      {children}
    </compensationReportFiltersContext.Provider>
  );
}
